import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {connect, useDispatch} from "react-redux";
import Loader from "./layout/loading/loader";
import {Formik} from "formik";
import './UsersNewOrEdit.css'
import {array, date, object, string} from 'yup';
import {Alert, Box, CircularProgress, Skeleton, Step, StepButton, Stepper} from "@mui/material";
import {useParams} from "react-router-dom";
import {campaignsActions} from "../../modules/campaigns/campaigns.actions";
import Button from "@mui/material/Button";
import CampaignDataField from "./campaigns/form/CampaignDataField";
import {usersActions} from "../../modules/users/users.actions";
import CampaignPatrimoineField from "./campaigns/form/CampaignPatrimoineField";
import CampaignOperateursField from "./campaigns/form/CampaignOperateursField";
import CampaignSummary from "./campaigns/form/CampaignSummary";
import {TravelExplore} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import {RRuleHelper} from "../../utils/RRuleHelper";
import CampaignOrderField from "./campaigns/form/CampaignOrderField";
import SubmitButton from "./layout/forms/SubmitButton";
import {prestationsActions} from "../../modules/prestations/prestations.actions.js";
import {
	extractId,
	patrimoinesApi,
	useLazyGetPatrimoinesLightByClientQuery,
} from "../../modules/patrimoines/patrimoines.api";
import {mapCampaignApiToForm, mapCampaignFormToPayload} from "../../modules/campaigns/campaigns.mapper";
import {sameResource} from "../../utils/apiResource";

// --- Constantes -------------------------------------------------------------

// Champs validés à chaque step. Permet :
//  - de valider par step (sans bloquer sur des champs non encore renseignés),
//  - d'afficher l'état d'erreur uniquement sur les steps touchés.
const STEP_FIELDS = {
	0: ['libelle', 'dateDebut', 'dateFin', 'client', 'prestation', 'formulaire'],
	1: ['patrimoine', 'conteneurs'],
	2: [], // ordre : pas de champ requis dédié
	3: ['chauffeur'],
};

const STEPS = [
	"Détail de la tournée",
	"Sélection du patrimoine",
	"Ordre de la tournée",
	"Sélection des opérateurs",
];

// Schéma de base : on ne déclare PAS `patrimoine`/`chauffeur` comme required ici
// car la validation est faite step par step (cf. STEP_FIELDS + validateStep).
// Cela évite d'afficher des erreurs sur des champs non encore atteints.
const campaignValidationSchema = object({
	libelle: string()
		.max(255, 'Le libellé ne doit pas dépasser 255 caractères')
		.required('Le libellé est requis'),
	dateDebut: date().required('La date de début est requise'),
	dateFin: date().required('La date de fin est requise'),
	conteneurs: array().nullable(),
	chauffeur: string().nullable(),
	client: string().nullable().required('Le client est requis'),
	prestation: string().nullable(),
	formulaire: string().required('Le formulaire est requis'),
	patrimoine: string().nullable(),
});

// --- Composant -------------------------------------------------------------

function CampaignsNewOrEdit({...props}) {
	const dispatch = useDispatch();
	const params = useParams();
	const urlId = params.id ? parseInt(params.id, 10) : null;
	const mode = urlId ? "edit" : "create";

	const [activeStep, setActiveStep] = useState(0);
	// Steps déjà visités → autorisés au clic sur le Stepper (StepButton)
	const [visitedSteps, setVisitedSteps] = useState(() => new Set([0]));


	// RTK Query : chargement *lazy* de la liste light des patrimoines.
	// Déclenché à l'entrée du step 2 (et préchargé au survol du bouton « Suivant »).
	const [
		triggerFetchPatrimoines,
		{
			data: patrimoinesLight = [],
			isFetching: patrimoinesFetching,
			isError: patrimoinesError,
		},
	] = useLazyGetPatrimoinesLightByClientQuery();

	// Pour éviter le double-fetch quand le user revient sur le step 2 :
	// on garde l'id client déjà chargé.
	const lastFetchedClientRef = useRef(null);

	// --- Initial values ------------------------------------------------------

	const initialValues = useMemo(() => {
		return mapCampaignApiToForm(props.campaign);
	}, [props.campaign]);

	// --- Chargements initiaux (clients / users / prestations) --------------

	useEffect(() => {
		if (mode === "edit" && urlId) {
			if (!props.campaign || props.campaign.id !== urlId) {
				dispatch(campaignsActions.fetchCampaign(urlId));
			}
		}
	}, [dispatch, urlId, mode, props.campaign?.id]);

	useEffect(() => {
		if (!props.clients && !props.loading) dispatch(usersActions.fetchClients());
		if (!props.users && !props.loading) dispatch(usersActions.fetchUsers());
		if (!props.prestations && !props.loading) dispatch(prestationsActions.fetchPrestations());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	// --- Patrimoines : chargement *lazy* -----------------------------------

	// Précharge (ou recharge) la liste light pour le client donné.
	const loadPatrimoinesForClient = useCallback((clientIri) => {
		if (!clientIri) return;
		const id = extractId(clientIri);
		if (!id) return;
		if (lastFetchedClientRef.current === id) return;
		lastFetchedClientRef.current = id;
		triggerFetchPatrimoines(id, /* preferCacheValue */ true);
	}, [triggerFetchPatrimoines]);

	// Précharge dès qu'on connaît le client (campagne éditée),
	// pour que le step 2 soit immédiat si l'utilisateur y accède.
	useEffect(() => {
		if (initialValues?.client) {
			loadPatrimoinesForClient(initialValues.client);
		}
	}, [initialValues?.client, loadPatrimoinesForClient]);

	// --- Stepper navigation -------------------------------------------------

	const goToStep = useCallback((next) => {
		setActiveStep(next);
		setVisitedSteps(prev => {
			if (prev.has(next)) return prev;
			const copy = new Set(prev);
			copy.add(next);
			return copy;
		});
	}, []);

	const validateStep = async (stepIndex, formik) => {
		const errs = await formik.validateForm();
		const fields = STEP_FIELDS[stepIndex] || [];

		// Validation manuelle complémentaire pour le step 1 (patrimoine + au moins 1 conteneur).
		const manual = {};
		if (stepIndex === 1) {
			if (!formik.values.patrimoine) manual.patrimoine = 'Le patrimoine est requis';
		}
		if (stepIndex === 3) {
			if (!formik.values.chauffeur) manual.chauffeur = 'Au moins un chauffeur doit être sélectionné';
		}

		const allErrs = {...errs, ...manual};
		const stepErrs = fields.filter(f => allErrs[f]);
		if (stepErrs.length > 0) {
			const touched = {};
			stepErrs.forEach(f => { touched[f] = true; });
			formik.setTouched({...formik.touched, ...touched}, true);
			return false;
		}
		return true;
	};

	const handleNextStep = async (formik) => {
		const ok = await validateStep(activeStep, formik);
		if (!ok) return;

		const next = activeStep + 1;

		// Au passage du step 0 → step 1, on lance le fetch des patrimoines si pas déjà fait.
		if (activeStep === 0 && formik.values.client) {
			loadPatrimoinesForClient(formik.values.client);
		}

		goToStep(next);
	};

	const handleBackStep = () => {
		goToStep(Math.max(0, activeStep - 1));
	};

	const handleStepButtonClick = (index) => {
		if (visitedSteps.has(index)) {
			setActiveStep(index);
		}
	};

	// Prefetch au survol du bouton « Suivant » depuis le step 0
	const prefetchPatrimoines = (clientIri) => {
		if (activeStep !== 0 || !clientIri) return;
		const id = extractId(clientIri);
		if (!id || lastFetchedClientRef.current === id) return;
		// On déclenche silencieusement, le cache sera prêt au moment du clic.
		dispatch(
			patrimoinesApi.util.prefetch
				? patrimoinesApi.util.prefetch('getPatrimoinesLightByClient', id, {force: false})
				: {type: 'noop'},
		);
	};

	const hasStepErrors = (index, errors, touched) => {
		const fields = STEP_FIELDS[index] || [];
		return fields.some(f => touched?.[f] && errors?.[f]);
	};

	// --- Changement de client -----------------------------------------------

	const handleClientChange = (clientIri) => {
		// Invalide la sélection patrimoine/conteneurs si le client change.
		lastFetchedClientRef.current = null;
		if (clientIri) {
			loadPatrimoinesForClient(clientIri);
		}
	};

	// --- Operateurs / Summary ----------------------------------------------

	const getOperateurs = () => {
		if (props.users) {
			return props.users[0]?.operateurs;
		}
		return [];
	};

	const getValuesForSummary = (values) => {
		const populatedValues = {};
		populatedValues.libelle = values.libelle;
		populatedValues.dateDebut = values.dateDebut;
		populatedValues.dateFin = values.dateFin;
		populatedValues.heureDebut = values.heureDebut;
		populatedValues.conteneurs = values.conteneurs;
		populatedValues.prestation = props.prestations?.find((p) => p['@id'] === values.prestation);
		populatedValues.client = props.clients?.find((c) => c['@id'] === values.client);
		populatedValues.formulaire = populatedValues.client?.forms?.find((f) => f['@id'] === values.formulaire);
		populatedValues.patrimoine = patrimoinesLight?.find((p) => sameResource(p, values.patrimoine))
			|| populatedValues.client?.patrimoines?.find((p) => sameResource(p, values.patrimoine));
		const operateurs = getOperateurs();
		populatedValues.chauffeur = operateurs?.find((o) => o['@id'] === values.chauffeur);
		if (values.equipier) {
			populatedValues.equipier = operateurs?.find((o) => o['@id'] === values.equipier);
		}
		populatedValues.rrule = RRuleHelper.buildRRuleString(values);
		return populatedValues;
	};

	// --- Submit -------------------------------------------------------------

	const handleSubmit = (values, formikHelpers) => {
		const payload = mapCampaignFormToPayload(values);

		const onDone = () => {
			formikHelpers.setSubmitting(false);
		};

		if (mode === "create") {
			dispatch(campaignsActions.createCampaign(payload)).then(onDone, () => formikHelpers.setSubmitting(false));
		} else {
			dispatch(campaignsActions.updateCampaign(payload, values.id)).then(onDone, () => formikHelpers.setSubmitting(false));
		}
	};

	if (props.loading || (mode === 'edit' && !props.campaign)) {
		return <Loader/>;
	}

	return (
		<>
			<PageTitle
				title={mode === "create" ? "Nouvelle tournée" : "Modifier la tournée"}
				icon={<TravelExplore/>}
			/>
			<Formik
				initialValues={initialValues}
				enableReinitialize={true}
				validationSchema={campaignValidationSchema}
				onSubmit={handleSubmit}
			>
				{(formik) => {
					const {
						values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting,
					} = formik;

					return (
						<form onSubmit={handleSubmit} className={"mb-2"}>
							<Stepper activeStep={activeStep} alternativeLabel sx={{mb: 2}} nonLinear>
								{STEPS.map((label, index) => {
									const visited = visitedSteps.has(index);
									return (
										<Step key={label} completed={false}>
											<StepButton
												onClick={() => handleStepButtonClick(index)}
												disabled={!visited}
												optional={hasStepErrors(index, errors, touched) ? (
													<span style={{color: '#d32f2f', fontSize: 12}}>Champs requis</span>
												) : null}
											>
												{label}
											</StepButton>
										</Step>
									);
								})}
							</Stepper>

							{activeStep === STEPS.length ? (
								<>
									<CampaignSummary values={getValuesForSummary(values)} errors={errors}/>
									<Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
										<Button
											variant={"contained"}
											color={"primary"}
											onClick={handleBackStep}
											sx={{mr: 1, color: 'white'}}
										>
											Retour
										</Button>
										<Box sx={{flex: '1 1 auto'}}/>
										<SubmitButton submitting={isSubmitting} label={"Enregistrer la tournée"} color={"secondary"}/>
									</Box>
								</>
							) : (
								<>
									{activeStep === 0 && (
										<CampaignDataField
											values={values}
											errors={errors}
											handleChange={handleChange}
											onClientSelected={handleClientChange}
											prestations={props.prestations}
											handleBlur={handleBlur}
											clients={props.clients}
										/>
									)}

									{activeStep === 1 && (
										patrimoinesFetching ? (
											<Box sx={{display: 'flex', flexDirection: 'column', gap: 1, p: 2}}>
												<Skeleton variant="text" width="40%"/>
												<Skeleton variant="rectangular" height={56}/>
												<Skeleton variant="rectangular" height={300}/>
											</Box>
										) : patrimoinesError ? (
											<Alert severity="error" sx={{my: 2}}>
												Impossible de charger les patrimoines de ce client.{' '}
												<Button
													size="small"
													onClick={() => {
														lastFetchedClientRef.current = null;
														loadPatrimoinesForClient(values.client);
													}}
												>
													Réessayer
												</Button>
											</Alert>
										) : (
											<CampaignPatrimoineField
												values={values}
												errors={errors}
												handleChange={handleChange}
												handleBlur={handleBlur}
												patrimoines={patrimoinesLight}
											/>
										)
									)}

									{activeStep === 2 && (
										<CampaignOrderField
											values={values}
											errors={errors}
											handleChange={handleChange}
											handleBlur={handleBlur}
											patrimoines={patrimoinesLight}
										/>
									)}

									{activeStep === 3 && (
										<CampaignOperateursField
											values={values}
											errors={errors}
											handleChange={handleChange}
											handleBlur={handleBlur}
											operateurs={getOperateurs()}
										/>
									)}

									<Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
										<Button
											variant={"contained"}
											color={"primary"}
											disabled={activeStep === 0}
											onClick={handleBackStep}
											sx={{mr: 1}}
										>
											Retour
										</Button>
										<Box sx={{flex: '1 1 auto'}}/>
										<Button
											onClick={() => handleNextStep(formik)}
											onMouseEnter={() => prefetchPatrimoines(values.client)}
											variant={"contained"}
											color={"secondary"}
											disabled={activeStep === 0 && patrimoinesFetching}
											sx={{color: 'white'}}
										>
											{activeStep === 0 && patrimoinesFetching && (
												<CircularProgress size={16} sx={{mr: 1, color: 'white'}}/>
											)}
											{activeStep === STEPS.length - 1 ? 'Terminer' : 'Suivant'}
										</Button>
									</Box>
								</>
							)}
						</form>
					);
				}}
			</Formik>
		</>
	);
}

const mapStateToProps = function (state) {
	return {
		campaign: state.campaigns.campaign,
		clients: state.users.clients,
		prestations: state.prestations.prestations,
		users: state.users.users,
		loading: state.campaigns.loading || state.users.loading,
		error: state.campaigns.error || state.users.error,
	};
};

export default connect(mapStateToProps)(CampaignsNewOrEdit);
